"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiService = void 0;
const Annotation_1 = require("../modules/annotation/components/Annotation");
const annotationService_1 = require("./services/annotationService");
const modelService_1 = require("./services/modelService");
const assetService_1 = require("./services/assetService");
const assetVersionType_1 = require("../enums/assetVersionType");
const modelFormat_1 = require("../enums/modelFormat");
const Toast_1 = require("../modules/toast/Toast");
const ViewerOptions_1 = require("../objects/ViewerOptions");
const three_1 = require("three");
const config_1 = require("../config");
const pubsub_js_1 = require("pubsub-js");
class ApiService {
    constructor(webgl, progressLayer, assetId, assetVersionType, tokenProvider) {
        this.webgl = webgl;
        this.assetId = assetId;
        this.tokenProvider = tokenProvider;
        this.progressLayer = progressLayer;
        this.auth = this.getAuth(assetVersionType);
        if (!ViewerOptions_1.ViewerOptions.autoload) {
            this.showAssetThumbnail(this.assetId);
        }
    }
    setTokenProvider(tokenProvider) {
        this.tokenProvider = tokenProvider;
    }
    getAuth(assetVersionType) {
        const needToken = assetVersionType === assetVersionType_1.AssetVersionType.MAIN || assetVersionType === assetVersionType_1.AssetVersionType.PRIVATE || assetVersionType === assetVersionType_1.AssetVersionType.REVIEW;
        const token = needToken ? this.authorizationToken() : null;
        const auth = { assetVersionType: assetVersionType, token };
        return auth;
    }
    authorizationToken() {
        if (!this.tokenProvider) {
            throw new Error("Token provider function is undefined");
        }
        const token = this.tokenProvider();
        if (typeof token !== 'string') {
            throw new Error("Token is incorrect");
        }
        return token;
    }
    /**
     * Get all needed data for viewer
     * @param assetVersionType
     */
    getInitialObjects(assetVersionType) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const auth = this.getAuth(assetVersionType);
                const sceneOverride = yield assetService_1.assetService.getScene(auth, this.assetId).catch((err) => { throw new Error(err); });
                if (sceneOverride === null || sceneOverride === void 0 ? void 0 : sceneOverride.data) {
                    // Change view background if option is defined
                    if (ViewerOptions_1.ViewerOptions.backgroundColor) {
                        sceneOverride.data.background.type = "COLOR" /* COLOR */;
                        sceneOverride.data.background.color = ViewerOptions_1.ViewerOptions.backgroundColor;
                    }
                    PubSub.publish("scene-override-change" /* SCENE_OVERRIDE_CHANGE */, sceneOverride.data);
                    PubSub.publish("lights-list" /* LIGHTS_LIST */, sceneOverride.data.lights);
                }
                else {
                    const color = ViewerOptions_1.ViewerOptions.backgroundColor ? ViewerOptions_1.ViewerOptions.backgroundColor : config_1.config.webgl.backgroundColor;
                    this.webgl.scene.background = new three_1.Color(color).convertLinearToSRGB();
                    (0, pubsub_js_1.publish)("initial-background-set" /* INITIAL_BACKGROUND_SET */, color);
                }
                const models = yield modelService_1.modelService.list(auth, this.assetId).catch((err) => { throw new Error(err); });
                if (!models || !models.models3d) {
                    return;
                }
                models.models3d.forEach((model) => __awaiter(this, void 0, void 0, function* () {
                    if (model.format && model.format === modelFormat_1.ModelFormat.MODEL_FORMAT_GLTF) {
                        this.showModel(auth, this.assetId, model, "low" /* LOW */);
                    }
                    if (assetVersionType !== assetVersionType_1.AssetVersionType.MAIN && assetVersionType !== assetVersionType_1.AssetVersionType.PRIVATE) {
                        this.showModelDetails(auth, this.assetId, model);
                    }
                    this.showAnnotation(auth, this.assetId, model);
                    this.showModel(auth, this.assetId, model, "high" /* HIGH */);
                }));
            }
            catch (error) {
                this.failed(error);
            }
        });
    }
    uploadThumbnail(formData) {
        return __awaiter(this, void 0, void 0, function* () {
            const auth = {
                assetVersionType: assetVersionType_1.AssetVersionType.PRIVATE,
                token: this.authorizationToken()
            };
            return assetService_1.assetService.uploadThumbnail(auth, this.assetId, formData);
        });
    }
    uploadImage(formData) {
        return __awaiter(this, void 0, void 0, function* () {
            const auth = { assetVersionType: assetVersionType_1.AssetVersionType.PRIVATE, token: this.authorizationToken() };
            return assetService_1.assetService.putImage(auth, this.assetId, formData);
        });
    }
    getImages() {
        return __awaiter(this, void 0, void 0, function* () {
            const token = this.authorizationToken();
            const auth = { assetVersionType: assetVersionType_1.AssetVersionType.PRIVATE, token };
            return assetService_1.assetService.getImages(auth, this.assetId);
        });
    }
    getImage(imageId) {
        return __awaiter(this, void 0, void 0, function* () {
            return assetService_1.assetService.getImage(this.auth, this.assetId, imageId);
        });
    }
    showAssetThumbnail(assetId) {
        return __awaiter(this, void 0, void 0, function* () {
            const assetThumbnailUrl = yield this.downloadAssetThumbnail(assetId);
            if (!assetThumbnailUrl) {
                console.log("Missing asset thumbnail url");
                return;
            }
            this.progressLayer.container.setStyle({
                'background-image': 'url("' + assetThumbnailUrl + '")',
                'background-repeat': 'no-repeat',
                'background-position': 'center',
                'background-size': 'cover'
            });
        });
    }
    downloadAssetThumbnail(assetId) {
        return __awaiter(this, void 0, void 0, function* () {
            return assetService_1.assetService.downloadThumbnail(this.auth, assetId);
        });
    }
    /**
     * Show model
     */
    showModel(auth, assetId, model, qualityType) {
        return __awaiter(this, void 0, void 0, function* () {
            const modelUrl = yield modelService_1.modelService.getPreviewUrl(auth, assetId, model.modelId, qualityType)
                .catch((err) => this.failed(err));
            if (!modelUrl || !modelUrl.previewUrl)
                throw new Error("Missing model preview url");
            this.webgl.loadModel(modelUrl.previewUrl, model, this.tokenProvider, qualityType)
                .catch((err) => this.failed(err))
                .finally(() => this.progressLayer.visible = false);
        });
    }
    /**
     * Show model details
     */
    showModelDetails(auth, assetId, model) {
        return __awaiter(this, void 0, void 0, function* () {
            const modelDetails = yield modelService_1.modelService.get(auth, assetId, model.modelId)
                .catch((err) => this.failed(err));
        });
    }
    /**
     * Show annotations
     */
    showAnnotation(auth, assetId, model) {
        return __awaiter(this, void 0, void 0, function* () {
            const annotations = yield annotationService_1.annotationService
                .list(auth, assetId, model.modelId)
                .catch((err) => this.failed(err));
            if (!annotations || !annotations.annotations || annotations.annotations.length === 0) {
                PubSub.publish("no-annotations" /* NO_ANNOTATIONS */);
                return;
            }
            annotations.annotations.sort((a, b) => {
                if (a.orderNumber < b.orderNumber) {
                    return -1;
                }
                if (a.orderNumber > b.orderNumber) {
                    return 1;
                }
                return 0;
            });
            annotations.annotations.map(ann => ann.orderNumber += 1);
            let newAnnotations;
            // NOTE: this needs to be changed
            // TODO: temporary solution to check if annotationControl exist,
            // in ViewType.LIGHT annotation control is not defined yet
            const annotationControl = document.getElementById('annotationControl');
            if (!annotationControl) {
                // Light menu
                newAnnotations = annotations.annotations.map((annotation) => new Annotation_1.Annotation(annotation, false));
            }
            else {
                newAnnotations = annotations.annotations.map((annotation) => new Annotation_1.Annotation(annotation));
            }
            // If AssetVersionType is set to MAIN || PRIVATE, '?mode=PRIVATE' must be added to every <img src="..."
            // inside each annotation content
            // not to every <img src=""> only to those that does not start with https://...
            if (auth.assetVersionType === assetVersionType_1.AssetVersionType.MAIN || auth.assetVersionType === assetVersionType_1.AssetVersionType.PRIVATE) {
                newAnnotations = this.convertAnnotationsImages(newAnnotations);
            }
            PubSub.publish("annotation-add-many" /* ANNOTATION_ADD_MANY */, newAnnotations);
        });
    }
    // adds '?mode=PRIVATE' to every <img src="..."
    // that does not start with 'https://..' inside each annotation content
    convertAnnotationsImages(annotations) {
        return annotations.map(annotation => {
            var _a, _b;
            if (!((_a = annotation === null || annotation === void 0 ? void 0 : annotation.content) === null || _a === void 0 ? void 0 : _a.includes('img'))) {
                return annotation;
            }
            let content = annotation.content;
            const result = (_b = content.match(/img src=".+?"/g)) === null || _b === void 0 ? void 0 : _b.map(url => { var _a; return (_a = url.match(/".+"/)) === null || _a === void 0 ? void 0 : _a[0]; }).map(url => url === null || url === void 0 ? void 0 : url.substring(1, (url === null || url === void 0 ? void 0 : url.length) - 1)).map(url => {
                if (url === null || url === void 0 ? void 0 : url.startsWith('https')) {
                    return { url, full: url };
                }
                return { url, full: `/api/storage${url}?mode=PRIVATE` };
            });
            result === null || result === void 0 ? void 0 : result.forEach(url => {
                content = content.replace(url.url + '', url.full);
            });
            annotation.content = content;
            return annotation;
        });
    }
    saveAnnotations(annotationList, modelId) {
        return __awaiter(this, void 0, void 0, function* () {
            let newAnnotations = [];
            for (const annotation of annotationList) {
                let newAnnotation = {
                    annotationPoint: {
                        x: annotation.annotationPoint.x,
                        y: annotation.annotationPoint.y,
                        z: annotation.annotationPoint.z,
                    },
                    cameraPoint: {
                        x: annotation.cameraPoint.x,
                        y: annotation.cameraPoint.y,
                        z: annotation.cameraPoint.z,
                    },
                    name: annotation.name,
                    content: annotation.imagePlaceholder + this.annotationContentImagesToIds(annotation.content),
                    url: annotation.url,
                    id: annotation.id,
                    manualId: annotation.manualId,
                    orderNumber: annotation.orderNumber - 1
                };
                newAnnotations.push(newAnnotation);
            }
            const annotations = yield annotationService_1.annotationService.save(newAnnotations, { assetVersionType: assetVersionType_1.AssetVersionType.PRIVATE, token: this.authorizationToken() }, this.assetId, modelId);
            return annotations;
        });
    }
    // Convert image urls in annotation content to only ids
    // example:
    //  <img src="/v1/assets/<asset-id>/images/<image-id>">
    //  TO
    //  <img src="<image-id>">
    annotationContentImagesToIds(annotationContent) {
        var _a;
        if (!annotationContent) {
            return '';
        }
        let content = annotationContent;
        if (!content.includes('img')) {
            return content;
        }
        const urls = (_a = content.match(/img src=".+?"/g)) === null || _a === void 0 ? void 0 : _a.map((url) => { var _a; return (_a = url.match(/".+"/)) === null || _a === void 0 ? void 0 : _a[0]; }).map((url) => (url === null || url === void 0 ? void 0 : url.substring(1, (url === null || url === void 0 ? void 0 : url.length) - 1)) || '');
        urls === null || urls === void 0 ? void 0 : urls.forEach((imageUrl) => {
            const url = imageUrl.replace('?mode=PRIVATE', '');
            const splitedUrl = url.split('/');
            const id = splitedUrl[splitedUrl.length - 1];
            content = content === null || content === void 0 ? void 0 : content.replace(imageUrl, id);
        });
        return content;
    }
    updateSceneOverride(sceneOverride) {
        return __awaiter(this, void 0, void 0, function* () {
            const token = this.authorizationToken();
            const auth = { assetVersionType: assetVersionType_1.AssetVersionType.PRIVATE, token: token };
            const body = { data: sceneOverride };
            return assetService_1.assetService.putScene(auth, this.assetId, body).catch((err) => this.failed(err));
        });
    }
    /**
     * On faild show error
     */
    failed(message) {
        this.progressLayer.stopLoader();
        Toast_1.Toast.create('error', message, 3000);
    }
}
exports.ApiService = ApiService;
